import React, { useEffect, useState, useCallback, useMemo } from "react";
import { Box, Typography } from "@mui/material";
import GenericDraggableTableView from "../../../Features/Table/GenericDraggableTable";
import { getColumnsConfig } from "../../../Features/Table/GenericDraggableTable/GenericDraggableTableFuncations";
import { MAX_COLUMN_SELECTION_COUNT } from "../../../Helper/DigitalTwin/DataFile";
import { usePagesContent, useSearchableFields, searchFieldsKey, useBaseEntityIdLabel } from "../../../Hooks/usePagesContent";
import {
  getFleetPerformanceAnalyticsList,
  getFleetPerformanceAnalyticsListActions,
} from "../../../Api/FleetPerformanceApi";
import { fetchFiltersDataObject } from "../../../Helper/Table/TableHelper";
import { useNavigate } from "react-router-dom";
import { encryptTheParams } from "../../../Helper/QueryParams/EncryptDecrypt";
import DeviceIDLink from "../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/DeviceIDLink";
import ProgressBarColumnView from "../../../Features/Table/GenericDraggableTable/Renderers/columnTypes/ProgressBarColumnView";
import { getSOHColor } from "../../../Helper/BatteryHealth/BatteryHealthHelper";
import {
  FLEET_PERFORMANCE_TABLE_KEYS,
  useFleetTableConfig,
} from "../fleetPerformanceTableConfig";
import { buildAggregatedMetricsExportColumns, buildAggregatedMetricsRow, EXPORT_DEVICE_ID_FIELD } from "./aggregatedMetricsRow";

// What the list request asks for whatever the columns are: the default sort's
// metric, and DistanceTotal, which this list has always requested. Every resolved
// column's own metric is appended by resolveSelect, so a configured column needs
// no entry here.
const BASE_SELECT = ["AveragekWhPerKm", "DistanceTotal"];
const BASE_META_INFO = [
  "licencePlatNumber",
  "deviceID",
  "originCountry",
  "originState",
  "originCity",
  "originDepot",
  "pingStatus",
];
const BASE_EXPORT_META_INFO = [
  EXPORT_DEVICE_ID_FIELD,
  "licencePlatNumber",
  "originCountry",
  "originState",
  "originCity",
  "originDepot",
];
const GROUP_BY = ["batteryID"];

const DEFAULT_SORT = [{ column: "AveragekWhPerKm", direction: "DESC" }];

/**
 * The FleetPerformance org meta key that can replace this table's columns.
 * Absent from an org's meta, TABLE_COLUMNS_CONFIG / TABLE_COLUMN_ORDER below
 * stand unchanged.
 */
const ORG_META_TABLE_KEY = FLEET_PERFORMANCE_TABLE_KEYS.ENERGY_CONSUMPTION_METRICS;

const SEARCHABLE_FIELDS = [
  { key: "licensePlateNo", title: "Bus Number" },
  { key: "deviceID", title: "Device ID" },
  { key: "vin", title: "Chassis Number" },
];

const TABLE_COLUMN_ORDER = [
  "licencePlatNumber",
  "AveragekWhPerKm",
  "MinkWhPerKm",
  "MaxkWhPerKm",
  "AveragekWhPerSoc",
  "MinkWhPerSoc",
  "MaxkWhPerSoc",
  "AverageKmPerSoc",
  "MinKmPerSoc",
  "MaxKmPerSoc",
  "TotalChargingDurationMinutes",
  "TotalDischargingDurationMinutes",
  "AverageSOH",
  "originDepot",
  "originCity",
  "originState",
  "originCountry",
];

const TABLE_COLUMNS_CONFIG = {
  licencePlatNumber: {
    title: "Bus Number",
    dataIndex: "licencePlatNumber",
    width: 200,
    valueRenderingType: "device-id-link",
    fixed: "left",
  },
  originDepot: {
    title: "Depot",
    dataIndex: "originDepot",
    width: 200,
    valueRenderingType: "text",
    decimalPlaces: 2,
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
  },
  originCity: {
    title: "City",
    dataIndex: "originCity",
    width: 200,
    valueRenderingType: "text",
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
  },
  originState: {
    title: "State",
    dataIndex: "originState",
    width: 200,
    valueRenderingType: "text",
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
  },
  originCountry: {
    title: "Country",
    dataIndex: "originCountry",
    width: 140,
    valueRenderingType: "text",
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
  },
  AveragekWhPerKm: {
    title: "kWh/km",
    dataIndex: "AveragekWhPerKm",
    width: 150,
    valueRenderingType: "smart-number",
    decimalPlaces: 2,
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
  },
  MinkWhPerKm: {
    title: "Min kWh/km",
    dataIndex: "MinkWhPerKm",
    width: 150,
    valueRenderingType: "smart-number",
    decimalPlaces: 2,
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
  },
  MaxkWhPerKm: {
    title: "Max kWh/km",
    dataIndex: "MaxkWhPerKm",
    width: 150,
    valueRenderingType: "smart-number",
    decimalPlaces: 2,
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
  },
  AveragekWhPerSoc: {
    title: "kWh/SOC",
    dataIndex: "AveragekWhPerSoc",
    width: 150,
    valueRenderingType: "smart-number",
    decimalPlaces: 2,
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
  },
  MinkWhPerSoc: {
    title: "Min kWh/SOC",
    dataIndex: "MinkWhPerSoc",
    width: 150,
    valueRenderingType: "smart-number",
    decimalPlaces: 2,
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
  },
  MaxkWhPerSoc: {
    title: "Max kWh/SOC",
    dataIndex: "MaxkWhPerSoc",
    width: 150,
    valueRenderingType: "smart-number",
    decimalPlaces: 2,
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
  },
  AverageKmPerSoc: {
    title: "km/SOC",
    dataIndex: "AverageKmPerSoc",
    width: 150,
    valueRenderingType: "smart-number",
    decimalPlaces: 2,
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
  },
  MinKmPerSoc: {
    title: "Min km/SOC",
    dataIndex: "MinKmPerSoc",
    width: 150,
    valueRenderingType: "smart-number",
    decimalPlaces: 2,
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
  },
  MaxKmPerSoc: {
    title: "Max km/SOC",
    dataIndex: "MaxKmPerSoc",
    width: 150,
    valueRenderingType: "smart-number",
    decimalPlaces: 2,
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
  },
  AverageSOH: {
    title: "Avg SOH (%)",
    dataIndex: "AverageSOH",
    width: 200,
    valueRenderingType: "progress-bar",
    decimalPlaces: 0,
    unitPostfix: "",
    defaultValue: "-",
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
  },
  TotalChargingDurationMinutes: {
    title: "Time Spent Charging",
    dataIndex: "TotalChargingDurationMinutes",
    width: 200,
    valueRenderingType: "duration",
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
  },
  TotalDischargingDurationMinutes: {
    title: "Time Spent Discharging",
    dataIndex: "TotalDischargingDurationMinutes",
    width: 200,
    valueRenderingType: "duration",
    sorter: true,
    sortDirections: ["ascend", "descend", "ascend"],
  }
};

const TABLE_FILTERS_CONFIG = [
  {
    key: "AverageSOH",
    type: "static",
    label: "Avg SOH",
    staticData: [],
    displayType: "range",
    unitOfMeasurement: "%"
  },
  {
    key: "vehicleSubType",
    type: "dynamic",
    label: "Bus Type",
    staticData: [],
    displayType: "multiSelectDropdown",
    queryParams: {
      table: "vehicleConfig",
      column: "vehicleSubType"
    }
  },
  {
    key: "AveragekWhPerKm",
    type: "static",
    label: "kWh/km",
    staticData: [],
    displayType: "range",
    unitOfMeasurement: "kWh/km"
  },
  {
    key: "MaxkWhPerKm",
    type: "static",
    label: "Max kWh/km",
    staticData: [],
    displayType: "range",
    unitOfMeasurement: "kWh/km"
  },
  {
    key: "MinkWhPerKm",
    type: "static",
    label: "Min kWh/km",
    staticData: [],
    displayType: "range",
    unitOfMeasurement: "kWh/km"
  },
  {
    key: "AveragekWhPerSoc",
    type: "static",
    label: "kWh/SOC",
    staticData: [],
    displayType: "range",
    unitOfMeasurement: "kWh/SOC"
  },
  {
    key: "MinkWhPerSoc",
    type: "static",
    label: "Min kWh/SOC",
    staticData: [],
    displayType: "range",
    unitOfMeasurement: "kWh/SOC"
  },
  {
    key: "MaxkWhPerSoc",
    type: "static",
    label: "Max kWh/SOC",
    staticData: [],
    displayType: "range",
    unitOfMeasurement: "kWh/SOC"
  },
  {
    key: "AverageKmPerSoc",
    type: "static",
    label: "km/SOC",
    staticData: [],
    displayType: "range",
    unitOfMeasurement: "km/SOC"
  },
  {
    key: "MinKmPerSoc",
    type: "static",
    label: "Min km/SOC",
    staticData: [],
    displayType: "range",
    unitOfMeasurement: "km/SOC"
  },
  {
    key: "MaxKmPerSoc",
    type: "static",
    label: "Max km/SOC",
    staticData: [],
    displayType: "range",
    unitOfMeasurement: "km/SOC"
  },
  {
    key: "addedOn",
    type: "static",
    label: "Onboarding Date",
    staticData: [],
    displayType: "dateCalender",
    queryParams: {}
  },
  {
    key: "ProjectName",
    type: "dynamic",
    label: "Project",
    staticData: [],
    displayType: "multiSelectDropdown",
    queryParams: {
      table: "batteryConfigList",
      column: "configName"
    }
  },
  {
    key: "protocolName",
    type: "dynamic",
    label: "Protocol",
    staticData: [],
    displayType: "multiSelectDropdown",
    queryParams: {
      table: "protocolMeta",
      column: "protocolName"
    }
  },
  {
    key: "TotalChargingDurationMinutes",
    type: "static",
    label: "Time Spent Charging (min)",
    staticData: [],
    displayType: "range",
    unitOfMeasurement: "min"
  },
  {
    key: "TotalDischargingDurationMinutes",
    type: "static",
    label: "Time Spent Discharging (min)",
    staticData: [],
    displayType: "range",
    unitOfMeasurement: "min"
  }
];

const AggregatedMetricsTable = ({ startDate, endDate, globalFilters = {} }) => {
  const pagesContent = usePagesContent();
  const searchFields = useSearchableFields(SEARCHABLE_FIELDS);
  const navigate = useNavigate();

  const { tableColumnsConfig, tableColumnOrder, resolveMetaInfo, resolveSelect } =
    useFleetTableConfig(ORG_META_TABLE_KEY, TABLE_COLUMNS_CONFIG, TABLE_COLUMN_ORDER);

  // The device id, then every resolved column; never the internal battery id. The
  // CSV keeps all of them, shown or not, as it always has.
  const deviceIdLabel = useBaseEntityIdLabel("Device ID");
  const exportColumns = useMemo(
    () => buildAggregatedMetricsExportColumns(tableColumnsConfig, tableColumnOrder, deviceIdLabel),
    [tableColumnsConfig, tableColumnOrder, deviceIdLabel]
  );

  const onBatteryIDClick = (record) => {
    const temp = { batteryID: record.batteryID, deviceID: record.deviceID, prevLink: '/fleet-performance', navigateFromTab: true, tabValue: 'performance' };
    encryptTheParams(temp, navigate, true);
  };

  const customRenderers = {
    'device-id-link': (record, config) => <DeviceIDLink record={record} config={config} onTextClick={() => onBatteryIDClick(record)} />,
    'smart-number': (record, config) => {
      const val = record?.[config?.dataIndex];
      const places = config?.decimalPlaces ?? 2;
      const display = typeof val === 'number'
        ? parseFloat(val.toFixed(places)).toString()
        : (val ?? '-');
      return (
        <Typography sx={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }} variant="fw400s14lh24c3D4B5A">
          {display}
        </Typography>
      );
    },
    'progress-bar': (record, config) => {
      const val = record?.[config?.dataIndex];
      const decimalPlaces = config?.decimalPlaces ?? 2;
      const barColor = getSOHColor(val);
      const unitPostFix = config?.unitPostfix ?? "";
      const defaultValue = config?.defaultValue ?? "-";
      return (
        <ProgressBarColumnView
          value={typeof val === 'number' ? val.toFixed(decimalPlaces) : 0}
          barColor={barColor}
          unitPostFix={unitPostFix}
          defaultText={defaultValue}
          record={record}
          config={config}
        />
      );
    },
    'duration': (record, config) => {
      const val = record?.[config?.dataIndex];
      if (typeof val !== 'number') {
        return (
          <Typography sx={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }} variant="fw400s14lh24c3D4B5A">
            {val ?? '-'}
          </Typography>
        );
      }
      const totalMinutes = Math.round(val);
      const hours = Math.floor(totalMinutes / 60);
      const minutes = totalMinutes % 60;
      let display = '';
      if (hours > 0 && minutes > 0) {
        display = `${hours} hr ${minutes} min`;
      } else if (hours > 0) {
        display = `${hours} hr`;
      } else {
        display = `${minutes} min`;
      }
      return (
        <Typography sx={{ textAlign: 'right', display: 'flex', justifyContent: 'flex-end' }} variant="fw400s14lh24c3D4B5A">
          {display}
        </Typography>
      );
    },
  };

  const [loading, setLoading] = useState(true);
  const [tableData, setTableData] = useState([]);
  const [total, setTotal] = useState(0);
  const [overallTotal, setOverallTotal] = useState(0);
  const [pageNumber, setPageNumber] = useState(1);
  const [rowsPerPage] = useState(20);
  const [sortBy, setSortBy] = useState(DEFAULT_SORT);

  // Custom Table Filters
  const [filters, setFilters] = useState({});
  const [selectedCustomFilters, setSelectedCustomFilters] = useState({});

  // Fetch dynamic filter options
  useEffect(() => {
    const initFilters = async () => {
      const filtersObj = await fetchFiltersDataObject(TABLE_FILTERS_CONFIG, filters);
      // Sort filters alphabetically by label
      const sortedFilters = Object.fromEntries(
        Object.entries(filtersObj).sort(([, a], [, b]) =>
          (a.label || '').localeCompare(b.label || '')
        )
      );
      setFilters(sortedFilters);
    };
    initFilters();
  }, []);

  // Column management
  const [initialColumns, setInitialColumns] = useState([]);
  const [tableColumns, setTableColumns] = useState([]);
  const [selectedColumnKeys, setSelectedColumnKeys] = useState([]);
  const [defaultSelectedColumnKeys, setDefaultSelectedColumnKeys] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);

  // Search management
  const [searchTerm, setSearchTerm] = useState({
    selectedDevices: [],
    selectedSearchColumn: null,
  });

  // Selection management
  const [totalSelectedRowKeys, setTotalSelectedRowKeys] = useState(new Set());
  const [isSelectAll, setIsSelectAll] = useState(false);

  // Setup table columns
  const setupTableColumns = useCallback(() => {
    const availableColumns = getColumnsConfig({
      tableColumns: tableColumnsConfig,
      tableColumnOrder,
      customRenderers,
    });

    setInitialColumns(availableColumns);
    const defaultSelected = availableColumns.slice(0, MAX_COLUMN_SELECTION_COUNT);
    const defaultKeys = defaultSelected.map((col) => col.dataIndex);
    setTableColumns(defaultSelected);
    setSelectedColumnKeys(defaultKeys);
    setDefaultSelectedColumnKeys(defaultKeys);
    setColumnOrder(defaultSelected.map((col) => col.headerLabel));
  }, [tableColumnsConfig, tableColumnOrder]);

  // Fetch table data
  useEffect(() => {
    const fetchData = async () => {
      if (!startDate || !endDate) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);

        const globalFilterParams = {
          hierarchicalLocation: globalFilters?.hierarchicalLocation,
          // fleets has to be forwarded or this table ignores the page-level
          // customer selection that the chart above it is already scoped by.
          fleets: globalFilters?.fleets,
          operationalStatus: globalFilters?.operationalStatus,
          allOperationalStatus: globalFilters?.allOperationalStatus,
        };

        const searchID = searchTerm.selectedSearchColumn || "batteryID";
        const filtersArray = (searchTerm?.selectedDevices?.length > 0)
          ? [{ name: searchID || 'batteryID', values: searchTerm.selectedDevices }]
          : [];

        if (selectedCustomFilters && Object.keys(selectedCustomFilters).length > 0) {
          Object.entries(selectedCustomFilters).forEach(([key, value]) => {
            if (Array.isArray(value) && value.length > 0) {
              filtersArray.push({ name: key, values: value });
            } else if (value && typeof value === 'object' && (value.min !== undefined || value.max !== undefined)) {
              filtersArray.push({ name: key, min: value.min, max: value.max });
            }
          });
        }

        const metaInfo = resolveMetaInfo(BASE_META_INFO);
        const params = {
          startDate,
          endDate,
          select: resolveSelect(BASE_SELECT, { groupBy: GROUP_BY, metaInfo }),
          filters: filtersArray,
          groupBy: GROUP_BY,
          granularity: null,
          metaInfo,
          skipPagination: false,
          pageNum: pageNumber,
          pageCount: rowsPerPage,
          ...(sortBy.length > 0
            ? { sortBy: JSON.stringify(sortBy) }
            : {}),
        };

        const response = await getFleetPerformanceAnalyticsList(
          globalFilterParams,
          params
        );
        const rawData = response?.response?.data || [];
        const listData = rawData.map(buildAggregatedMetricsRow);
        const responseTotal =
          response?.response?.paginationInfo?.total ??
          response?.response?.total?.total ??
          listData.length;

        setTableData(listData);
        setTotal(responseTotal);
      } catch (error) {
        console.error("Error fetching aggregated metrics:", error);
        setTableData([]);
        setTotal(0);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [startDate, endDate, globalFilters, pageNumber, rowsPerPage, sortBy, searchTerm, selectedCustomFilters, resolveMetaInfo, resolveSelect]);

  // Reset pagination when filters/date changes
  useEffect(() => {
    setPageNumber(1);
    setSortBy(DEFAULT_SORT);
    // Through an updater that keeps the previous object when it is already
    // cleared. A fresh literal is a new identity and searchTerm is a fetch
    // dependency, so an unconditional reset fired a second identical list
    // request one render later, on mount and on every filter change.
    setSearchTerm((prev) =>
      prev.selectedDevices.length === 0 && prev.selectedSearchColumn === null
        ? prev
        : { selectedDevices: [], selectedSearchColumn: null }
    );
  }, [startDate, endDate, globalFilters]);

  /* Fetch the "global" total whenever the page-level context changes.
  This runs without any table-level filters so overallTotal always reflects
  "no. of vehicles match the current global filters" regardless of what
  search / custom filters the user has applied to the table. */
  useEffect(() => {
    const fetchOverallTotal = async () => {
      if (!startDate || !endDate) return;
      try {
        const globalFilterParams = {
          hierarchicalLocation: globalFilters?.hierarchicalLocation,
          // fleets has to be forwarded or this table ignores the page-level
          // customer selection that the chart above it is already scoped by.
          fleets: globalFilters?.fleets,
          operationalStatus: globalFilters?.operationalStatus,
          allOperationalStatus: globalFilters?.allOperationalStatus,
        };
        const response = await getFleetPerformanceAnalyticsList(globalFilterParams, {
          startDate,
          endDate,
          select: ["AveragekWhPerKm"],
          filters: [],
          groupBy: ["batteryID"],
          granularity: null,
          metaInfo: [],
          skipPagination: false,
          pageNum: 1,
          pageCount: 1,
        });
        const globalTotal =
          response?.response?.paginationInfo?.total ??
          response?.response?.total?.total ??
          0;
        setOverallTotal(globalTotal);
      } catch (error) {
        console.error("Error fetching overall total:", error);
      }
    };
    fetchOverallTotal();
  }, [startDate, endDate, globalFilters]);

  // Initialize columns
  useEffect(() => {
    setupTableColumns();
  }, [setupTableColumns]);

  // Used by the download and mail actions, which must be scoped exactly like the
  // on-screen list above.
  const buildGlobalFilters = () => ({
    hierarchicalLocation: globalFilters?.hierarchicalLocation,
    fleets: globalFilters?.fleets,
    operationalStatus: globalFilters?.operationalStatus,
    allOperationalStatus: globalFilters?.allOperationalStatus,
  });

  // Apply table action (download / email) — mirrors FleetChartConfig pattern
  const applyTableAction = async (actionType, extraParams = {}) => {
    if (!startDate || !endDate) return actionType === "download" ? [] : null;

    const globalFilterParams = buildGlobalFilters();

    const searchID = searchTerm.selectedSearchColumn || "batteryID";
    const filtersArray = (searchTerm?.selectedDevices?.length > 0)
      ? [{ name: searchID || 'batteryID', values: searchTerm.selectedDevices }]
      : [];

    if (selectedCustomFilters && Object.keys(selectedCustomFilters).length > 0) {
      Object.entries(selectedCustomFilters).forEach(([key, value]) => {
        if (Array.isArray(value) && value.length > 0) {
          filtersArray.push({ name: key, values: value });
        } else if (value && typeof value === 'object' && (value.min !== undefined || value.max !== undefined)) {
          filtersArray.push({ name: key, min: value.min, max: value.max });
        }
      });
    }

    const metaInfo = resolveMetaInfo(BASE_EXPORT_META_INFO);
    const apiParams = {
      actionType,
      groupBy: GROUP_BY,
      select: resolveSelect(BASE_SELECT, { groupBy: GROUP_BY, metaInfo }),
      metaInfo,
      startDate,
      endDate,
      filters: filtersArray,
      resourceIDs: isSelectAll ? [] : Array.from(totalSelectedRowKeys),
      selectAll: isSelectAll,
      columns: exportColumns.columns,
      columnOrder: exportColumns.columnOrder,
      ...(sortBy.length > 0 ? { sortBy: JSON.stringify(sortBy) } : {}),
      ...(actionType === "mail" && {
        emailTo: extraParams.emailTo,
        emailCc: extraParams.emailCc,
        subject: extraParams.subject,
        body: extraParams.body,
      }),
    };

    const res = await getFleetPerformanceAnalyticsListActions(
      globalFilterParams,
      apiParams
    );
    if (res && res.responseStatus?.code === 200) {
      return res.response?.data || (actionType === "download" ? [] : null);
    }
    return actionType === "download" ? [] : null;
  };

  // CSV Download
  const getCSVData = async () => {
    return applyTableAction("download");
  };

  const getCSVFileName = () => {
    const formattedStartDate = new Date(startDate).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    const formattedEndDate = new Date(endDate).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
    return `Aggregated_Metrics_${formattedStartDate}-${formattedEndDate}.csv`;
  };

  // Send Email
  const onSendEmail = async ({ sendTo, sendCc, subject, body }) => {
    return applyTableAction("mail", {
      emailTo: sendTo,
      emailCc: sendCc,
      subject,
      body,
    });
  };

  // Row Selection
  const onSelectChange = (record, selected) => {
    const updatedSelectedKeys = new Set(totalSelectedRowKeys);
    if (selected) {
      updatedSelectedKeys.add(record.id);
    } else {
      updatedSelectedKeys.delete(record.id);
      if (isSelectAll) {
        setIsSelectAll(false);
      }
    }
    setTotalSelectedRowKeys(updatedSelectedKeys);
  };

  const onSelectAllChange = (selected, selectedRows) => {
    setIsSelectAll(selected);
    setTotalSelectedRowKeys(
      selected ? new Set(selectedRows.map((row) => row?.id)) : new Set()
    );
  };

  // Column Management
  const onColumnOrderChange = (newTableColumns) => {
    setColumnOrder(newTableColumns.map((col) => col.headerLabel));
  };

  const applySelectedColumns = (newSelectedKeys) => {
    const filteredColumns = initialColumns.filter((columnConfig) =>
      newSelectedKeys.includes(columnConfig.dataIndex)
    );
    setTableColumns(filteredColumns);
    setSelectedColumnKeys(newSelectedKeys);
    setColumnOrder(filteredColumns.map((col) => col.headerLabel));
  };

  // Pagination
  const handlePageChange = (page) => {
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
    setPageNumber(page);
  };

  // Clear Selection
  const handleClearSelection = () => {
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  };

  // Sorting
  const handleSortChange = (key, order) => {
    const direction = order === "asc" ? "ASC" : "DESC";
    const newSortBy = [{ column: key, direction }];
    setSortBy(newSortBy);
    setPageNumber(1);
    setTotalSelectedRowKeys(new Set());
    setIsSelectAll(false);
  };

  const getCurrentSorting = () => {
    if (sortBy && sortBy.length > 0) {
      const firstSort = sortBy[0];
      return {
        sortBy: firstSort.column,
        orderBy: firstSort.direction.toLowerCase(),
      };
    }
    return {};
  };

  const getCountText = () => {
    return overallTotal || "";
  };

  const getFilteredCount = () => {
    const hasSearchFilters = searchTerm?.selectedDevices?.length > 0;
    const hasCustomFilters = selectedCustomFilters && Object.keys(selectedCustomFilters).length > 0;
    if (!hasSearchFilters && !hasCustomFilters) {
      return false;
    }
    return total || 0;
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Box>
        <Typography
          variant="fw400s20c000"
          sx={{
            lineHeight: "28px",
            fontWeight: 300,
          }}
        >
          Aggregated Metrics Across Vehicles
        </Typography>
      </Box>

      <GenericDraggableTableView
        // The search inside seeds its selected key once (TableSearch.jsx:155); remount when
        // the org's search list arrives with the page meta. Unchanged for orgs without one.
        key={searchFieldsKey(searchFields)}
        title="Aggregated Data"
        showPrimaryButton={false}
        count={getCountText()}
        filteredCount={getFilteredCount()}
        tableColumns={tableColumns}
        selectedColumns={selectedColumnKeys}
        tableData={tableData}
        availableEmailDomains={
          pagesContent?.metaInfo?.globalConfig?.["email.domains"] ||
          pagesContent?.metaInfo?.availableEmailDomains ||
          []
        }
        initialColumns={initialColumns}
        applySelectedColumns={applySelectedColumns}
        defaultSelectedColumns={defaultSelectedColumnKeys}
        isLoading={loading}
        pageNumber={pageNumber}
        rowsPerPage={rowsPerPage}
        total={total}
        disablePagination={false}
        disableSearch={false}
        searchableFields={searchFields}
        applySearch={(selectedDevices, selectedSearchColumn) => {
          setSearchTerm({ selectedDevices, selectedSearchColumn });
          setPageNumber(1);
          setTotalSelectedRowKeys(new Set());
          setIsSelectAll(false);
        }}
        disableFiltersSettings={false}
        filters={filters}
        selectedFilters={selectedCustomFilters}
        applyFilters={(f) => {
          setSelectedCustomFilters(f);
          setPageNumber(1);
          setTotalSelectedRowKeys(new Set());
          setIsSelectAll(false);
        }}
        getCSVData={getCSVData}
        getCSVFileName={getCSVFileName}
        getCsvColumnSettings={() => ({ columnOrder: exportColumns.columnOrder })}
        onSendEmail={onSendEmail}
        columnOrder={columnOrder}
        onColumnOrderChange={onColumnOrderChange}
        applySort={handleSortChange}
        onPageChange={handlePageChange}
        initialSorting={getCurrentSorting()}
        onClearSelection={handleClearSelection}
        emptyMessage={searchTerm.selectedDevices.length > 0 || Object.keys(selectedCustomFilters).length > 0 ? "No matches found for filters applied" : "No aggregated metrics data available"}
        containerStyles={{
          backgroundColor: "#FFFFFF",
          borderRadius: "8px",
          border: "1px solid #E6E6E6",
          padding: "12px 24px",
          overflow: "visible",
          maxWidth: "calc(100vw - 150px)",
        }}
        getTotalRowSelection={() => Array.from(totalSelectedRowKeys)}
        getTotalRowSelectionCount={() =>
          isSelectAll ? total : totalSelectedRowKeys.size
        }
        rowSelection={{
          selectedRowKeys: Array.from(totalSelectedRowKeys),
          onSelect: onSelectChange,
          onSelectAll: onSelectAllChange,
        }}
        maxColumnsAllowed={MAX_COLUMN_SELECTION_COUNT}
      />
    </Box>
  );
};

export default AggregatedMetricsTable;